import CloseIcon from '@mui/icons-material/Close'
import { IconButton } from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'

const TRANSITION_MS = 300

/** Drawers montados, del más antiguo al más reciente; Escape solo cierra el último. */
const openStack: object[] = []

export interface DrawerProps {
    open: boolean
    onClose: () => void
    title?: React.ReactNode
    icon?: React.ReactNode
    actions?: React.ReactNode
    children?: React.ReactNode
    width?: number | string
    bare?: boolean
}

const Drawer = ({ open, onClose, title, icon, actions, children, width = 800, bare = false }: DrawerProps) => {
    const onCloseRef = useRef(onClose)
    onCloseRef.current = onClose
    const [rendered, setRendered] = useState(open)
    const [visible, setVisible] = useState(false)

    useEffect(() => {
        let cancelled = false
        if (open) {
            setRendered(true)
            const frame = requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (!cancelled) setVisible(true)
                })
            })
            return () => {
                cancelled = true
                cancelAnimationFrame(frame)
            }
        }
        setVisible(false)
        const timer = setTimeout(() => {
            if (!cancelled) setRendered(false)
        }, TRANSITION_MS)
        return () => {
            cancelled = true
            clearTimeout(timer)
        }
    }, [open])

    useEffect(() => {
        if (!rendered) return undefined
        const token = {}
        openStack.push(token)
        const previousOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && openStack[openStack.length - 1] === token) onCloseRef.current()
        }
        window.addEventListener('keydown', onKey)
        return () => {
            openStack.splice(openStack.indexOf(token), 1)
            document.body.style.overflow = previousOverflow
            window.removeEventListener('keydown', onKey)
        }
    }, [rendered])

    if (!rendered) return null

    return ReactDOM.createPortal(
        <div className="fixed inset-0 z-50 flex justify-end">
            <div
                className={`absolute inset-0 bg-[rgba(10,12,20,0.5)] transition-opacity duration-300 ease-out ${visible ? 'opacity-100' : 'opacity-0'}`}
                onClick={onClose}
            />
            <aside
                className={`relative z-10 flex h-full w-full transform flex-col bg-k-surface text-k-ink shadow-[-16px_0_48px_rgba(10,12,20,0.25)] transition-transform duration-300 ease-out ${visible ? 'translate-x-0' : 'translate-x-full'}`}
                style={{ maxWidth: width }}
            >
                {bare ? children : (
                    <>
                        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-k-line bg-k-sunken px-5 py-4">
                            <div className="flex min-w-0 items-center gap-3">
                                {icon && (
                                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-tone-blue text-tone-blue-ink">
                                        {icon}
                                    </span>
                                )}
                                <h3 className="truncate text-lg font-semibold text-k-ink">{title}</h3>
                            </div>
                            <div className="flex shrink-0 items-center gap-2">
                                {actions}
                                <IconButton title="Cerrar" onClick={onClose} sx={{ color: 'rgb(var(--k-muted))', '&:hover': { backgroundColor: 'rgb(var(--k-hover))' } }}>
                                    <CloseIcon />
                                </IconButton>
                            </div>
                        </div>
                        <div className="flex-1 overflow-auto bg-k-bg p-5">
                            {children}
                        </div>
                    </>
                )}
            </aside>
        </div>,
        document.body
    )
}

export default Drawer
